/**
 * Reading the portal's permission list.
 *
 * Login returns `permissions` as a FLAT array of permission-code strings — not
 * the two-sided { admin: {...}, crm: {...} } object the role-management screens
 * use. A code is built server-side as:
 *
 *     `${side}:${module}:${action}`                 flat module
 *     `${side}:${module}/${subModule}:${action}`    nested module
 *
 *     side    "admin" | "crm"
 *     action  view | create | edit | delete | export | approve | reject | cancel
 *
 * e.g. "admin:Partner Management:view"
 *      "crm:Bookings/Booking Management:view"
 *
 * ⚠️ These checks decide what the UI SHOWS. They are not a security boundary:
 * the list is client-side data and can be edited by whoever is sitting at the
 * browser. Every protected route is enforced server-side; hiding a button here
 * is a convenience, never a control.
 */

export const PERMISSION_SIDES = ["admin", "crm"];

export const PERMISSION_ACTIONS = [
  "view",
  "create",
  "edit",
  "delete",
  "export",
  "approve",
  "reject",
  "cancel",
];

/**
 * Build the code the server would have stored for this leaf.
 *
 * Mirrors buildModuleKey + buildPermissionCode in the backend's
 * utils/constants/rolePermissions.ts. Keep the separators identical — ":"
 * between the three parts and "/" before a sub-module — or every lookup misses
 * and the UI silently renders as though nothing is granted.
 */
export const buildPermissionCode = (side, module, subModule, action) => {
  const moduleKey = subModule ? `${side}:${module}/${subModule}` : `${side}:${module}`;
  return `${moduleKey}:${action}`;
};

/**
 * Does this permission list grant an action?
 *
 * Call with or without a sub-module:
 *   can(perms, "admin", "Partner Management", null, "view")
 *   can(perms, "crm", "Bookings", "Booking Management", "view")
 */
export const can = (permissions, side, module, subModule, action) => {
  if (!Array.isArray(permissions) || !permissions.length) return false;
  return permissions.includes(buildPermissionCode(side, module, subModule, action));
};

/**
 * Is any action granted anywhere under a module? Drives nav visibility, where
 * the question is "should this menu entry exist at all" rather than which
 * specific action is allowed.
 *
 * Matches on the module prefix, so a nested module is covered by its parent:
 * "crm:Bookings" also matches "crm:Bookings/Booking Management:view".
 */
export const canAccessModule = (permissions, side, module) => {
  if (!Array.isArray(permissions) || !permissions.length) return false;
  const prefix = `${side}:${module}`;
  return permissions.some((code) => code === prefix || code.startsWith(`${prefix}:`) || code.startsWith(`${prefix}/`));
};

/**
 * Bind a permission list once so call sites read as `perms.can("admin", …)`
 * instead of threading the array through every component.
 */
export const permissionChecker = (permissions) => ({
  can: (side, module, subModule, action) => can(permissions, side, module, subModule, action),
  canAccessModule: (side, module) => canAccessModule(permissions, side, module),
  list: permissions || [],
});
